import _ from 'lodash';
import { ErroredModel } from '../types/ErroredModel';

export type StartConditionType = 'manual' | 'date' | 'dependency' | 'offset';

export type Startable = ErroredModel & {
  uuid?: string | null;
  start_date: string | null;
  start_date_offset: number | null;
  depends_on_uuid: string | null;
};

const StartConditionField = {
  DATE: 'start_date',
  OFFSET: 'start_date_offset',
  DEPENDS: 'depends_on_uuid',
} as const;

type StartConditionField = (typeof StartConditionField)[keyof typeof StartConditionField];

function requiredFieldFor(type: StartConditionType): StartConditionField | null {
  if (type === 'date') return StartConditionField.DATE;
  if (type === 'offset') return StartConditionField.OFFSET;
  if (type === 'dependency') return StartConditionField.DEPENDS;
  return null;
}

function isStartConditionValuePresent(task: Startable, type: StartConditionType): boolean {
  if (type === 'manual') return true;

  if (type === 'date') {
    return _.isString(task.start_date) && _.trim(task.start_date).length > 0;
  }

  if (type === 'offset') {
    return _.isNumber(task.start_date_offset) && !_.isNaN(task.start_date_offset);
  }

  if (type === 'dependency') {
    const hasUuid = !_.isNil(task.depends_on_uuid) && _.trim(String(task.depends_on_uuid)).length > 0;
    const notSelf = _.isNil(task.uuid) || task.depends_on_uuid !== task.uuid;
    return hasUuid && notSelf;
  }

  return false;
}

export function deriveInitialStartCondition(task: Startable): StartConditionType {
  if (!_.isNil(task.depends_on_uuid)) return 'dependency';
  if (!_.isNil(task.start_date_offset)) return 'offset';
  if (!_.isNil(task.start_date)) return 'date';
  return 'manual';
}

export function validateStartCondition(task: Startable, type: StartConditionType): { valid: boolean; missingField?: StartConditionField } {
  const field = requiredFieldFor(type);
  if (!field) return { valid: true };
  const ok = isStartConditionValuePresent(task, type);
  return ok ? { valid: true } : { valid: false, missingField: field };
}

export function shouldDisableSubmitForStartCondition(task: Startable, type: StartConditionType): boolean {
  return !validateStartCondition(task, type).valid;
}
